import { IAdobeInteraction } from "@abbvie-epi/common/adobe-analytics";
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import gsap from "gsap";
import { AnalyticsHelperService } from "../../services/analytics-helper.service";
import { HeroService } from "../../services/hero.service";
import { development, screeningTool } from "./header.config";

@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"]
})
export class HeaderComponent implements OnInit, AfterViewInit {
    public developmentTabs: any = development;
    public screeningToolTabs: any = screeningTool;

    public activeRoute: string;
    public activeRouteIndex: number;
    public activeRouteFragment: any;
    public activeDropdowns: any = {
        development: false,
        "the screening tool": false
    };

    public aboveHero: boolean = true;

    public mobileNavIsShowing: boolean = false;
    public mobileDropdown: string = "";

    public activeUnderlineIndex: number;
    public underlineDisplay: { width: number; offset: number };
    private navRoutes: string[] = ["home", "what-is-epi", "development", "screening-tool-landing", "faqs"];

    @ViewChild("developmentDropdown", { static: true }) developmentDropdown: ElementRef;
    @ViewChild("toolDropdown", { static: true }) toolDropdown: ElementRef;
    @ViewChild("header", { static: true }) header: ElementRef;
    @ViewChild("hero", { static: true }) hero: ElementRef;

    @ViewChild("hamburgerMenu", { static: true }) hamburgerMenu: ElementRef;
    @ViewChild("drawer", { static: true }) drawer: ElementRef;
    @ViewChild("mobileOverlay", { static: true }) mobileOverlay: ElementRef;

    @ViewChild("devDropdown", { static: true }) devDropdown: ElementRef;
    @ViewChild("screeningDropdown", { static: true }) screeningDropdown: ElementRef;

    @ViewChildren("devTabList") devTabList: QueryList<ElementRef>;
    public devTabs: any[] = [];

    @ViewChildren("screeningTabList") screeningTabList: QueryList<ElementRef>;
    public screeningTabs: ElementRef[] = [];

    @ViewChildren("navLink") navLinks: QueryList<ElementRef<HTMLAnchorElement>>;

    constructor(
        private router: Router,
        public heroService: HeroService,
        public activatedRoute: ActivatedRoute,
        private analyticsHelper: AnalyticsHelperService
    ) {}

    ngOnInit(): void {
        this.getRoute();
        this.activeRoute = this.currentRoute.split("#")[0];

        setTimeout(() => {
            this.activeRouteUnderlinePosition();
        }, 0);
    }

    ngAfterViewInit() {
        this.hero = this.heroService.getHero();

        this.devTabList.forEach((element) => {
            this.devTabs.push(element.nativeElement);
        });
        this.screeningTabList.map((elementRef) => {
            this.screeningTabs.push(elementRef.nativeElement);
        });
    }

    private getRoute() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.activeRouteUnderlinePosition();
                this.resetMobileMenu();

                this.activeRoute = this.currentRoute.split("#")[0];

                setTimeout(() => {
                    this.hero = this.heroService.getHero();
                    gsap.to(this.header.nativeElement, { backgroundColor: "rgba(5, 21, 53, 0.09)", duration: 0 });
                    this.mobileNavIsShowing = false;
                }, 0);
            }
        });

        this.activatedRoute.fragment.subscribe((value) => {
            this.activeRouteFragment = value;
        });
    }

    public routeTo(route: string) {
        this.router.navigate([route]);
    }

    // Mobile
    public expandMobileNav() {
        this.mobileNavIsShowing = true;
        gsap.to(this.drawer.nativeElement, { left: "80%", duration: 0.1 });
        gsap.to(this.drawer.nativeElement, { left: "0%", duration: 0.35, delay: 0.1, ease: "power2" });
        gsap.to(this.hamburgerMenu.nativeElement, {
            right: "83.44vw",
            duration: 0.35,
            delay: 0.1,
            ease: "power2"
        });
        gsap.to(this.mobileOverlay.nativeElement, { opacity: 0.6, visibility: "visible", duration: 0.45 });

        this.trackMenuButton("header-hamburger-menu-open");
    }

    public collapseMobileNav() {
        this.mobileNavIsShowing = false;
        gsap.to(this.drawer.nativeElement, { left: "80%", duration: 0.35, ease: "power2.in" });
        gsap.to(this.drawer.nativeElement, { left: "100%", duration: 0.1, delay: 0.35 });
        gsap.to(this.hamburgerMenu.nativeElement, {
            right: "5.56vw",
            duration: 0.35,
            ease: "power2.in"
        });
        gsap.to(this.mobileOverlay.nativeElement, { opacity: 0, duration: 0.45 });
        gsap.to(this.mobileOverlay.nativeElement, { visibility: "hidden", delay: 0.45 });

        this.trackMenuButton("header-hamburger-menu-close");
    }

    public openMobileDropdown(dropdown: string) {
        // Open dropdown animation
        if (dropdown !== this.mobileDropdown) {
            if (dropdown === "development") {
                gsap.fromTo(
                    this.devDropdown.nativeElement,
                    { height: 0 },
                    { height: "auto", duration: 0.6, ease: "power2", visibility: "visible" }
                );
                gsap.to(this.devTabs, { opacity: 1, duration: 0.6, stagger: 0.2, delay: 0.2 });
                gsap.to(this.devTabs, { visibility: "visible" });
            } else if (dropdown === "screening-tool") {
                gsap.fromTo(
                    this.screeningDropdown.nativeElement,
                    { height: 0 },
                    { height: "auto", duration: 0.6, ease: "power2", visibility: "visible" }
                );
                gsap.to(this.screeningTabs, { opacity: 1, duration: 0.6, stagger: 0.2, delay: 0.2 });
                gsap.to(this.screeningTabs, { visibility: "visible" });
            }
        }

        // Close dropdown animation
        if (this.mobileDropdown !== "" || this.mobileDropdown === dropdown) {
            if (this.mobileDropdown === "development") {
                gsap.to(this.devDropdown.nativeElement, { height: 0, duration: 0.6, delay: 0.6, ease: "power2" });
                gsap.to(this.devDropdown.nativeElement, { visibility: "hidden", delay: 1.2 });
                gsap.to(this.devTabs, { opacity: 0, duration: 0.2, stagger: -0.2 });
                gsap.to(this.devTabs, { visibility: "hidden", delay: 0.6 });
            } else if (this.mobileDropdown === "screening-tool") {
                gsap.to(this.screeningDropdown.nativeElement, { height: 0, duration: 0.6, delay: 0.6, ease: "power2" });
                gsap.to(this.screeningDropdown.nativeElement, { visibility: "hidden", delay: 1.2 });
                gsap.to(this.screeningTabs, { opacity: 0, duration: 0.6, stagger: -0.2 });
                gsap.to(this.screeningTabs, { visibility: "hidden", delay: 0.6 });
            }
        }

        // Set the active variable
        if (this.mobileDropdown === dropdown) {
            this.mobileDropdown = "";
        } else {
            this.mobileDropdown = dropdown;
        }
    }

    private resetMobileMenu() {
        if (this.mobileNavIsShowing) {
            gsap.to(this.drawer.nativeElement, { left: "100%", duration: 0 });
            gsap.to(this.mobileOverlay.nativeElement, { opacity: 0, visibility: "hidden" });
            gsap.to(this.hamburgerMenu.nativeElement, { right: "5.56vw", duration: 0 });
        }
        if (this.mobileDropdown === "development") {
            gsap.to(this.devDropdown.nativeElement, { height: 0 });
            gsap.to(this.devTabs, { visibility: "hidden", opacity: 0 });
        } else if (this.mobileDropdown === "screening-tool") {
            gsap.to(this.screeningDropdown.nativeElement, { height: 0 });
            gsap.to(this.screeningTabs, { visibility: "hidden", opacity: 0 });
        }

        this.mobileDropdown = "";
    }

    public goToRoute(event: any, route: string, extraArgs: { fragment?: string; trackingId?: string }) {
        event.stopPropagation();

        if (extraArgs && extraArgs.trackingId) {
            this.track(extraArgs.trackingId);
        }

        this.router.navigate([route], { fragment: extraArgs.fragment || undefined });
    }

    public trackMenuButton(id: string) {
        const overrides = this.getBaseHeaderAnalyticsOverrides();

        this.analyticsHelper.trackInteraction(id, overrides || undefined);
    }

    public track(id: string) {
        const overrides = this.getBaseHeaderAnalyticsOverrides();

        overrides["menu"] = {
            location: "header",
            level: "1"
        };

        this.analyticsHelper.trackInteraction(id, overrides || undefined);
    }

    private getBaseHeaderAnalyticsOverrides(): IAdobeInteraction {
        return {
            link: {
                type: "internal"
            },
            component: {
                name: "header",
                position: "header",
                type: "navigation"
            },
            content: {
                type: "link"
            },
            interaction: {
                type: "link"
            }
        };
    }

    // Scroll effect
    @HostListener("window:scroll")
    onWindowScroll() {
        if (this.hero) {
            if (
                window.scrollY > this.hero.nativeElement.offsetHeight - this.header.nativeElement.offsetHeight &&
                this.aboveHero
            ) {
                this.aboveHero = false;
                gsap.to(this.header.nativeElement, { backgroundColor: "#05173b", duration: 0.4 });
            } else if (
                window.scrollY < this.hero.nativeElement.offsetHeight - this.header.nativeElement.offsetHeight &&
                !this.aboveHero
            ) {
                this.aboveHero = true;
                gsap.to(this.header.nativeElement, {
                    backgroundColor: "rgba(5, 21, 53, 0.09)",
                    duration: 0.2
                });
            }
        }
    }

    public setUnderlinePosition(newIndex: number) {
        this.activeUnderlineIndex = newIndex;
        this.underlineDisplay = this.calculateUnderlineOffset(newIndex);
    }

    public activeRouteUnderlinePosition() {
        this.activeRouteIndex = this.findNavRouteIndex();
        this.setUnderlinePosition(this.activeRouteIndex);
    }

    public calculateUnderlineOffset(selectedIndex: number): { width: number; offset: number } {
        let foundActive = false;

        const newValues = this.navLinks.toArray().reduce(
            (acc, el: ElementRef<HTMLAnchorElement>, i) => {
                const element = el.nativeElement;
                const width: number = element.getBoundingClientRect().width;

                if (i === selectedIndex) {
                    foundActive = true;

                    return {
                        width: width,
                        offset: acc.offset
                    };
                } else if (!foundActive) {
                    // add padding
                    return { width: acc.width, offset: acc.offset + width };
                }

                return acc;
            },
            { width: 0, offset: 0 }
        );

        return newValues;
    }

    private get currentRoute() {
        return this.router.url;
    }

    private findNavRouteIndex(): number {
        const routePath = this.currentRoute.split(/[\/#]/)[1];
        return this.navRoutes.findIndex((route) => route === routePath);
    }

    @HostListener("window:resize", ["$event"])
    onWindowResize() {
        this.underlineDisplay = this.calculateUnderlineOffset(this.activeRouteIndex);
    }
}
